import { Link } from '@tanstack/react-router';
import { CalendarClock, EyeOff, Repeat, Send } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ScheduleTimeline,
  ScheduleTimelineDate,
  ScheduleTimelineRun,
} from '@nucleus-commerce/api-client/admin';
import type { Formatters } from '@nucleus-commerce/i18n';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';

type Entry =
  | { kind: 'run'; at: string; run: ScheduleTimelineRun }
  | { kind: 'date'; at: string; date: ScheduleTimelineDate };

export interface UpcomingListProps {
  timeline: ScheduleTimeline;
  /** Names for store ids; an unknown id shows as "a store". */
  storeNames: ReadonlyMap<string, string>;
  /** Days and times are read on this clock. */
  format: Formatters;
  /**
   * The schedule detail's tab: the timeline was read for one schedule (`?scheduleId=`), so runs
   * show their cycle instead of a link and the truncation note does not suggest a store.
   */
  forSchedule?: boolean;
}

/**
 * The next weeks as a list grouped by day: predicted rotation runs (which stores, which cycle)
 * and one-shot publish / unpublish dates. No calendar grid (decision 25).
 */
export function UpcomingList({
  timeline,
  storeNames,
  format,
  forSchedule = false,
}: UpcomingListProps) {
  const { t } = useTranslation('schedules');
  const days = useMemo(() => {
    const entries: Entry[] = [
      ...timeline.runs.map((run): Entry => ({ kind: 'run', at: run.at, run })),
      ...timeline.dates.map((date): Entry => ({ kind: 'date', at: date.at, date })),
    ].sort((a, b) => a.at.localeCompare(b.at));
    const byDay = new Map<string, Entry[]>();
    for (const entry of entries) {
      const key = format.dateKey(entry.at);
      byDay.set(key, [...(byDay.get(key) ?? []), entry]);
    }
    return [...byDay.entries()];
  }, [format, timeline]);
  const storeName = (id: string) => storeNames.get(id) ?? t('errors.aStore');

  return (
    <div className="flex flex-col gap-4">
      {timeline.truncated ? (
        <Alert role="status">
          <AlertDescription>
            {/* The schedule tab has no store picker, so it cannot suggest narrowing by store. */}
            {forSchedule ? t('upcoming.truncatedSchedule') : t('upcoming.truncated')}
          </AlertDescription>
        </Alert>
      ) : null}
      {days.length === 0 ? (
        <p className="text-muted-foreground flex items-center justify-center gap-2 rounded-md border p-6 text-sm">
          <CalendarClock aria-hidden className="size-4" />
          {forSchedule ? t('upcoming.emptySchedule') : t('upcoming.empty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-4" data-tour="schedules.upcoming.list">
          {days.map(([day, entries]) => (
            <li key={day} className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">
                {format.date(entries[0]?.at ?? day, 'full')}
              </h3>
              <ul className="divide-y rounded-md border">
                {entries.map((entry) =>
                  entry.kind === 'run' ? (
                    <li
                      key={`run:${entry.run.scheduleId}:${entry.run.cycle}`}
                      className="flex items-start gap-3 px-3 py-2 text-sm"
                    >
                      <Repeat
                        aria-hidden
                        className="text-muted-foreground mt-0.5 size-4 shrink-0"
                      />
                      <span
                        className="text-muted-foreground w-14 shrink-0 tabular-nums"
                        title={format.utc(entry.at)}
                      >
                        {format.time(entry.at)}
                      </span>
                      <span className="min-w-0 flex-1">
                        {forSchedule ? (
                          <span className="font-medium">
                            {t('upcoming.cycle', { cycle: entry.run.cycle })}
                          </span>
                        ) : (
                          <Link
                            to="/org/schedules/$scheduleId"
                            params={{ scheduleId: entry.run.scheduleId }}
                            className="font-medium hover:underline"
                          >
                            {entry.run.scheduleName}
                          </Link>
                        )}
                        {forSchedule ? null : (
                          <span className="text-muted-foreground">
                            {' · '}
                            {t('upcoming.cycle', { cycle: entry.run.cycle })}
                          </span>
                        )}
                        <span className="text-muted-foreground block text-xs">
                          {entry.run.storeIds.map(storeName).join(', ')}
                        </span>
                      </span>
                    </li>
                  ) : (
                    <li
                      key={`date:${entry.date.storeProductId}:${entry.date.action}`}
                      className="flex items-start gap-3 px-3 py-2 text-sm"
                    >
                      {entry.date.action === 'publish' ? (
                        <Send
                          aria-hidden
                          className="text-muted-foreground mt-0.5 size-4 shrink-0"
                        />
                      ) : (
                        <EyeOff
                          aria-hidden
                          className="text-muted-foreground mt-0.5 size-4 shrink-0"
                        />
                      )}
                      <span
                        className="text-muted-foreground w-14 shrink-0 tabular-nums"
                        title={format.utc(entry.at)}
                      >
                        {format.time(entry.at)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <Link
                          to="/s/$storeId/catalog/$storeProductId"
                          params={{
                            storeId: entry.date.storeId,
                            storeProductId: entry.date.storeProductId,
                          }}
                          className="font-medium hover:underline"
                        >
                          {entry.date.title}
                        </Link>
                        <span className="text-muted-foreground">
                          {' · '}
                          {t(`upcoming.dateAction.${entry.date.action}`)}
                        </span>
                        <span className="text-muted-foreground block text-xs">
                          {storeName(entry.date.storeId)}
                        </span>
                      </span>
                    </li>
                  ),
                )}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
